import React, { useEffect } from "react";
import {
  History,
  ChevronRight,
  Palmtree,
  CirclePlus,
  Loader2,
  Hospital,
} from "lucide-react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import dayjs from "dayjs";
import { Timeoff, TimeoffCategoryType } from "../../../types"; // Assuming your types are in this file
import { addDays, eachDayOfInterval, format, subDays } from "date-fns";
import { setSelectedTimeoffForView } from "../../../redux/features/timeoff/slice";
import { getTimeOffRequestList } from "../../../redux/features/timeoff";
import {  getTimeOffStatusCheck } from "../../../utils/timeoff";
import { DateTimeUtil, formatHoursRounded } from "../../../utils";
import { getAssignedTimeoffCategories } from "../../../redux/features/common";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { setGlobalBackHandler, clearGlobalBackHandler } from "../../../hooks";

const TimeOffScreen: React.FC = () => {
  const { isLoadingTimeOffRequests, fetchedTimeoffRequests } = useSelector(
    (state: RootState) => state.timeoff
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setSourcePath } = useNavigationHistory();
  const user = useSelector((state: RootState) => state.user.user);
  const [todayRequests, setTodayRequests] = React.useState<Timeoff[]>([]);
  const [upcomingRequests, setUpcomingRequests] = React.useState<Timeoff[]>([]);
  const [pastRequests, setPastRequests] = React.useState<Timeoff[]>([]);
  useEffect(() => {
    dispatch(getAssignedTimeoffCategories());
  }, [dispatch]);

  useEffect(() => {
    if (user?.id) {
      console.log("inside useEffect");

      const dateRange = getDateRange();

      const getTimeOffRequestsBody = {
        start_date: dateRange[0],
        end_date: dateRange[dateRange.length - 1],
        employee_id: user.id.toString(),
        category_code: "",
      };

      dispatch(getTimeOffRequestList(getTimeOffRequestsBody));
    }
  }, [user, dispatch]); // Runs only when `user` is available
  const getDateRange = (): string[] => {
    const today = new Date();
    const start = subDays(today, 30);
    const end = addDays(today, 30);

    return eachDayOfInterval({ start, end }).map((date) =>
      format(date, "yyyy-MM-dd")
    );
  };

  const navToRequestTimeoff = () => {
    navigate("/m-request-timeoff");
  };

  const formatDateRange = (start: string, end: string): string => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const startMonth = format(startDate, "MMM");
    const endMonth = format(endDate, "MMM");

    const startDay = format(startDate, "d");
    const endDay = format(endDate, "d");

    if (startDay === endDay) {
      return `${startMonth} ${startDay}`;
    }

    return startMonth === endMonth
      ? `${startMonth} ${startDay} - ${endDay}`
      : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  };

  useEffect(() => {
    if (!fetchedTimeoffRequests) return;

    const today = DateTimeUtil.formatDate(new Date(), "yyyy-MM-dd");

    const todayFiltered = fetchedTimeoffRequests.filter((request) => {
      if (!request.start_date || !request.end_date) return false;

      return (
        DateTimeUtil.IsSameDayWithDayjsUtc(request.start_date, today) ||
        DateTimeUtil.IsSameDayWithDayjsUtc(request.end_date, today) ||
        (DateTimeUtil.IsBeforeWithDayjsUtc(request.start_date, today) &&
          DateTimeUtil.IsAfterWithDayjsUtc(request.end_date, today))
      );
    });

    const upcomingFiltered = fetchedTimeoffRequests
      .filter((request) => {
        if (!request.start_date || !request.end_date) return false;

        const isStartAfterToday = DateTimeUtil.IsAfterWithDayjsUtc(
          request.start_date,
          today
        );
        const isEndAfterToday = DateTimeUtil.IsAfterWithDayjsUtc(
          request.end_date,
          today
        );
        const isOngoing =
          DateTimeUtil.IsBeforeWithDayjsUtc(request.start_date, today) &&
          DateTimeUtil.IsAfterWithDayjsUtc(request.end_date, today);

        return (isStartAfterToday || isEndAfterToday) && !isOngoing;
      })
      .sort((a, b) =>
        DateTimeUtil.DifferenceInDaysWithDayjsUtc(b.start_date!, a.start_date!)
      )
      .slice(0, 2);

    const pastFiltered = fetchedTimeoffRequests
      .filter((request) => {
        if (!request.start_date) return false;
        return DateTimeUtil.IsBeforeWithDayjsUtc(request.start_date, today);
      })
      .sort((a, b) =>
        DateTimeUtil.DifferenceInDaysWithDayjsUtc(b.start_date!, a.start_date!)
      )
      .slice(0, 2);

    setTodayRequests(todayFiltered);
    setUpcomingRequests(upcomingFiltered);
    setPastRequests(pastFiltered);
  }, [fetchedTimeoffRequests]);
  useEffect(() => {
    dispatch(setTopNavNameTitle("Time Off"));
    
    // Set the source path for back navigation
    setSourcePath("/mobile-dashboard");
    
    // Set up global back handler to go back to mobile dashboard
    setGlobalBackHandler(() => {
      navigate("/mobile-dashboard");
      return true; // prevent default back behavior
    });
    
    // Clean up when component unmounts
    return () => {
      clearGlobalBackHandler();
    };
  }, [dispatch, navigate, setSourcePath]);

  const navToTimeoffDetails = (timeoff: Timeoff) => {
    dispatch(setSelectedTimeoffForView(timeoff));
    navigate("/m-timeoff-details");
  };
  const {
    assignedTimeoffCategories: timeoffCategories,
    isLoadingAssignedCategories,
  } = useSelector((state: RootState) => state.common);

  const TimeOffBalanceCard = ({
    timeOffBalanceByCategory,
  }: {
    timeOffBalanceByCategory: TimeoffCategoryType[];
  }) => {
    return (
      <>
        <div className="grid grid-cols-2 gap-4 ">
          {timeOffBalanceByCategory &&
            timeOffBalanceByCategory.length > 0 &&
            timeOffBalanceByCategory.map((category, index) => (
              <div
                key={index}
                className="flex flex-col items-start p-4 min-w-full max-w-full h-24 bg-blue-100 text-blue-500 rounded-2xl"
              >
                <span className="text-sm">
                  <Hospital />
                </span>
                <span className="text-md font-medium  w-full truncate whitespace-nowrap overflow-hidden">
                  {category?.category_name || "Unknown Category"}
                </span>
                <span className="text-sm  font-medium w-full truncate whitespace-nowrap overflow-hidden">
                  {" "}
                  {formatHoursRounded(category.time_off_hours_balance) ||
                    "N/A"}{" "}
                  {category.track_time_in}
                </span>
              </div>
            ))}
        </div>

        {/* <div className="grid grid-cols-1 gap-4 pt-4">
          {timeOffBalanceByCategory &&
            timeOffBalanceByCategory.length > 0 &&
            timeOffBalanceByCategory.map((category, index) => (
              <div>
                <p className="font-medium text-gray-900">
                  {" "}
                  {category?.category_name || "Unknown Category"}
                </p>
                <p className="text-sm text-gray-600">
                  {" "}
                  {formatHoursRounded(category.time_off_hours_balance) ||
                    "N/A"}{" "}
                  {category.track_time_in}
                </p>
              </div>
            ))}
        </div> */}
      </>
    );
  };

  if (isLoadingTimeOffRequests || isLoadingAssignedCategories) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }



  return (
    <div className="max-w-md mx-auto p-4 min-h-full">
      {/* Header */}
      <div className="mb-2">
        {timeoffCategories && (
          <TimeOffBalanceCard timeOffBalanceByCategory={timeoffCategories} />
        )}
      </div>
      <div className="">
        {/* <p className="text-2xl font-semibold text-gray-700">Time-off</p> */}
        <button
          className="flex justify-center w-full items-center bg-blue-600 text-white text-center py-2 rounded-md"
          onClick={navToRequestTimeoff}
        >
          {/* <CirclePlus className="mr-2" /> */}
          Request Time Off
        </button>
      </div>

      {/* Today's Time Off Section */}
      <section className="mb-6 mt-4">
        <h2 className="text-lg font-semibold text-gray-700 mb-2">
          Ongoing Time Off
        </h2>
        {todayRequests.length > 0 ? (
          <div className="space-y-1">
            {todayRequests.map((request, index) => {
              return (
                <div
                  key={`${request.time_off_request_id}-${index}`}
                  className="flex items-center justify-between p-3 cursor-pointer border rounded-md border-gray-200 "
                  onClick={() => navToTimeoffDetails(request)}
                >
                  <div className="flex flex-row items-start space-x-3 ">
                    {/* Icon inside a rounded square */}
                    {getTimeOffStatusCheck(request.status)}
                    <div className="p-2 flex-col w-full items-center justify-center">
                      <div className="text-md font-bold text-blue-600">
                        {request?.category_name}
                      </div>

                      {/* Time Off Details */}
                      <div className="flex  justify-between">
                        <div className="text-gray-900 font-md">
                          {formatDateRange(
                            request.start_date!,
                            request.end_date!
                          )}
                          {/* {getApprovalStatusCheck(request.status)}{" "} */}
                        </div>
                        <div className=" space-x-1 text-gray-600 text-md font-bold">
                          {request?.status}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mt-4 text-gray-500">You have no ongoing time off.</p>
        )}
      </section>

      {/* Upcoming Time Off Section */}
      <section className="mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-2">
          Upcoming Time Off
        </h2>
        {upcomingRequests.length > 0 ? (
          <div className="space-y-1">
            {upcomingRequests.map((request, index) => {
              return (
                <div
                  key={`${request.time_off_request_id}-${index}`}
                  className="flex items-center justify-between p-3 cursor-pointer border-b border-stone-2000"
                  onClick={() => navToTimeoffDetails(request)}
                >
             

                  <div className="flex flex-row items-start space-x-3 ">
                  {getTimeOffStatusCheck(request.status)}
                    <div className="p-2 flex-col w-full items-center justify-center">
                      <div className="text-md font-bold text-blue-600">
                        {request?.category_name}
                      </div>

                      {/* Time Off Details */}
                      <div className="flex  justify-between">
                        <div className="text-gray-900 font-md">
                          {formatDateRange(
                            request.start_date!,
                            request.end_date!
                          )}
                          {/* {getApprovalStatusCheck(request.status)}{" "} */}
                        </div>
                        <div className=" space-x-1 text-gray-600 text-md font-bold">
                          {request?.status}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Chevron for navigation */}
                  <ChevronRight className="text-gray-400 w-5 h-5" />
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500">You have no upcoming time off.</p>
        )}
        {upcomingRequests.length >= 2 && (
          <div
            className="flex items-center gap-2 mt-4 bg-white"
            onClick={() => navigate("/m-view-all-upcoming-timeoff")}
          >
            <History className="w-5 h-5 text-stone-500" />
            <p className="text-gray-700 font-medium text-m">
              View all upcoming time off
            </p>
          </div>
        )}
      </section>

      {/* History Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-700 mb-2">History</h2>
        <div className="space-y-1">
          {pastRequests.length > 0 ? (
            pastRequests.map((request, index) => {
         
              return (
                <div
                  key={`${request.time_off_request_id}-${index}`}
                  className="flex items-center justify-between p-3 cursor-pointer border rounded-md border-gray-200 "
                  onClick={() => navToTimeoffDetails(request)}
                >
              
                  <div className="flex flex-row items-start space-x-3 ">
    
                            {getTimeOffStatusCheck(request.status)}{" "}
                    <div className="p-2 flex-col w-full items-center justify-center">
                      <div className="text-md font-bold text-blue-600">
                        {request?.category_name}
                      </div>

                      {/* Time Off Details */}
                      <div className="flex  justify-between">
                        <div className="text-gray-900 font-md">
                          {formatDateRange(
                            request.start_date!,
                            request.end_date!
                          )}
                        </div>
                        <div className=" space-x-1 text-gray-600 text-md font-bold">
                          {request?.status}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Chevron for navigation */}
                  <ChevronRight className="text-gray-400 w-5 h-5" />
                </div>
              );
            })
          ) : (
            <p className="text-gray-500">No past time-off records available.</p>
          )}
        </div>
        {pastRequests.length >= 2 && (
          <div
            className="flex items-center gap-2 mt-4 bg-white"
            onClick={() => navigate("/m-view-all-past-timeoff")}
          >
            <History className="w-5 h-5 text-stone-500" />
            <p className="text-gray-700 font-medium text-m">View history</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default TimeOffScreen;
